import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { BarChart3, Check, FileText, Lightbulb, Lock, Mail, Scale, ShieldCheck, User, type LucideIcon } from "lucide-react";
import { toast } from "sonner";
import { emailSchema, type EmailFormValues } from "../lib/schemas";
import type { SimulationInput } from "../lib/types";
import { trackEvent } from "@/lib/analytics";


interface Props {
  simulation: SimulationInput;
  onUnlock: (email: string) => void;
}

const PREVIEW_ITEMS: { icon: LucideIcon; title: string; desc: string }[] = [
  { icon: BarChart3, title: "Projection sur 10 ans", desc: "Cash-flow cumulé, valorisation" },
  { icon: Scale, title: "Régime réel vs micro-BIC", desc: "Lequel est meilleur pour vous" },
  { icon: Lightbulb, title: "3 optimisations personnalisées", desc: "Spécifiques à votre bien" },
  { icon: FileText, title: "Rapport PDF complet", desc: "À télécharger immédiatement" },
];

const EMAIL_FLAG_KEY = "rentalmnp:email_captured";

export function EmailPaywall({ simulation, onUnlock }: Props) {
  const [success, setSuccess] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { prenom: "", email: "", consent: false },
  });

  const onSubmit = handleSubmit(async (values) => {
    // Lazy: @supabase/supabase-js (~15 KiB) téléchargé uniquement au submit
    const { supabase } = await import("@/integrations/supabase/client");

    // Enregistrement du lead dans Lovable Cloud
    // Aucune donnée de simulation envoyée — reste en LocalStorage
    const { error } = await supabase.from("leads").insert({
      email: values.email.trim().toLowerCase(),
      prenom: values.prenom?.trim() || null,
      consent: values.consent,
      source: "email_paywall",
    });

    // Doublon (23505) = déjà inscrit, on traite comme un succès silencieux
    if (error && error.code !== "23505") {
      // eslint-disable-next-line no-console
      console.error("Lead insert failed:", error);
      toast.error("Une erreur est survenue. Merci de réessayer.");
      return;
    }

    trackEvent("email_captured");

    try {
      window.localStorage.setItem(EMAIL_FLAG_KEY, "1");
    } catch {
      /* no-op */
    }

    setSuccess(true);
    setTimeout(() => onUnlock(values.email), 700);
  });

  return (
    <section className="mx-auto max-w-6xl px-6 py-10 lg:px-12">
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-md">
        {/* Header */}
        <div className="border-b border-border bg-secondary/60 px-6 py-4">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-primary" />
            <h3 className="text-base font-bold text-foreground">
              Analyse approfondie (gratuite par email)
            </h3>
          </div>
        </div>

        {/* Blurred preview en background + form au-dessus en flux normal */}
        <div className="relative">
          {/* Background flouté absolu — étiré pour couvrir toute la hauteur du form */}
          <div
            className="pointer-events-none absolute inset-0 grid gap-3 overflow-hidden p-6 sm:grid-cols-2"
            aria-hidden
            style={{ filter: "blur(5px)", userSelect: "none" }}
          >
            {PREVIEW_ITEMS.map((p) => {
              const I = p.icon;
              return (
                <div key={p.title} className="rounded-xl border border-border bg-secondary/40 p-4">
                  <I className="h-6 w-6 text-primary" aria-hidden />
                  <p className="mt-2 font-semibold text-foreground">{p.title}</p>
                  <p className="text-sm text-muted-foreground">{p.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Voile + form en flux normal pour que la hauteur s'adapte au contenu */}
          <div className="relative flex items-center justify-center bg-gradient-to-b from-background/40 via-background/85 to-background p-4 sm:p-8">
            <div className="w-full max-w-md rounded-2xl border border-border bg-background p-6 shadow-xl">
              {success ? (
                <div className="flex flex-col items-center gap-3 py-4 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/15">
                    <Check className="h-6 w-6 text-success" />
                  </div>
                  <p className="font-semibold text-foreground">C'est parti !</p>
                  <p className="text-sm text-muted-foreground">
                    Vérifiez votre boîte mail. Déblocage de l'analyse...
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-4" noValidate>
                  <div className="text-center">
                    <h4 className="flex items-center justify-center gap-2 text-lg font-bold text-foreground">
                      <Mail className="h-5 w-5 text-primary" aria-hidden />
                      Recevez votre analyse complète par email
                    </h4>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Plus la version PDF prête à imprimer
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="prenom-input"
                      className="mb-1 block text-sm font-medium text-foreground"
                    >
                      Prénom <span className="text-xs font-normal text-muted-foreground">(optionnel)</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <input
                        id="prenom-input"
                        type="text"
                        placeholder="Camille"
                        autoComplete="given-name"
                        {...register("prenom")}
                        className="block w-full rounded-lg border border-input bg-background py-2.5 pl-10 pr-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="email-input"
                      className="mb-1 block text-sm font-medium text-foreground"
                    >
                      Votre email <span className="text-danger">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <input
                        id="email-input"
                        type="email"
                        placeholder="vous@exemple.fr"
                        autoComplete="email"
                        {...register("email")}
                        className="block w-full rounded-lg border border-input bg-background py-2.5 pl-10 pr-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-xs text-danger">{errors.email.message}</p>
                    )}
                  </div>

                  <label className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
                    <input
                      type="checkbox"
                      {...register("consent")}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-input text-primary focus:ring-primary/30"
                    />
                    <span>
                      J'accepte de recevoir mon analyse LMNP et la newsletter hebdomadaire
                      (désabonnement en 1 clic à tout moment).{" "}
                      <span className="text-danger">*</span>
                    </span>
                  </label>
                  {errors.consent && (
                    <p className="-mt-2 text-xs text-danger">{errors.consent.message}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-primary-dark disabled:opacity-60"
                  >
                    Recevoir mon analyse →
                  </button>

                  <div className="rounded-lg border border-border bg-secondary/40 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
                    <ShieldCheck className="mr-1 inline h-3 w-3 text-success" />
                    Vos données ne sont utilisées que pour vous envoyer votre analyse et la
                    newsletter. Jamais partagées avec des tiers.{" "}
                    <Link
                      to="/confidentialite"
                      className="font-medium text-primary hover:underline"
                    >
                      Voir notre politique de confidentialité
                    </Link>
                    .
                  </div>

                  {/* Hidden field used to satisfy unused-var lint */}
                  <input type="hidden" value={simulation.region} readOnly />
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
